import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormGroup, FormsModule } from '@angular/forms';
import {
  BreadcrumbItem,
  QaiBreadcrumbListComponent,
  QaiLoadingComponent,
  QaiTabDirective,
  QaiTabsComponent,
} from '@qai/ui';
import { CommonModule } from '@angular/common';
import { GeneralDetailsComponent } from './general-details/general-details.component';
import { AssigneesContentComponent } from './assignees-content/assigness-content.component';
import { SimulationDetailsComponent } from './simulations-content/simulations-content.component';
import { AssignmentReactiveFormService } from '../services/assignment-reactive-form.service';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { Subscription } from 'rxjs';
import { IAssignmentDetails } from 'src/app/interfaces/self-serve/assignment-manager/responses/iassignment-details';

@Component({
  selector: 'assignment-details',
  templateUrl: './assignment-details.component.html',
  styleUrls: ['./assignment-details.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    QaiBreadcrumbListComponent,
    QaiTabsComponent,
    QaiTabDirective,
    CommonModule,
    GeneralDetailsComponent,
    AssigneesContentComponent,
    SimulationDetailsComponent,
    QaiLoadingComponent,
  ],
})
export class AssignmentDetailsComponent implements OnInit, OnDestroy {
  assignmentName: string = '';
  moduleAssignmentUuid: string = '';
  loading: boolean = true;
  form!: FormGroup;
  lastSaved: string | null = null;
  private formSubscription?: Subscription;
  private updatedAtSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private assignmentFormService: AssignmentReactiveFormService,
    private assignmentsService: AssignmentsService
  ) {}

  ngOnInit(): void {
    this.moduleAssignmentUuid = this.route.snapshot.paramMap.get('uuid')!;

    this.loadAssignmentData();
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.updatedAtSubscription?.unsubscribe();
    this.assignmentFormService.deleteAssignment(this.moduleAssignmentUuid);
  }

  private loadAssignmentData(): void {
    this.assignmentsService
      .getAssignmentDetails(this.moduleAssignmentUuid)
      .subscribe({
        next: (apiData: IAssignmentDetails) => {
          this.assignmentFormService.initializeAssignmentForm(
            this.moduleAssignmentUuid,
            apiData
          );
          this.assignmentName = apiData.name;
          this.subscribeToFormAndUpdatedAt();
        },
        error: (error) => {
          console.error('Error loading assignment data:', error);
        },
      });
  }

  private subscribeToFormAndUpdatedAt(): void {
    this.formSubscription?.unsubscribe();
    this.updatedAtSubscription?.unsubscribe();

    this.formSubscription = this.assignmentFormService
      .getForm(this.moduleAssignmentUuid)
      .subscribe((form) => {
        this.form = form;
        this.loading = false;

        const detailsGroup = this.form.get('details') as FormGroup;

        if (detailsGroup) {
          const nameControl = detailsGroup.get('moduleAssignmentName');
  
          if (nameControl) {
            this.assignmentName = nameControl.value;
  
            nameControl.valueChanges.subscribe((newName: string) => {
              this.assignmentName = newName;
            });
          }
        } 
      });

    this.updatedAtSubscription = this.assignmentFormService
      .getUpdatedAt$(this.moduleAssignmentUuid)
      .subscribe((timestamp) => {
        this.lastSaved = timestamp;
      });
  }

  getBreadcrumbItems(): BreadcrumbItem[] {
    return [
      {
        text: 'Assignments',
        href: '/self-serve',
      },
      {
        text: this.assignmentName,
        active: true,
      },
    ];
  }
}
